// features/item-filter/drillDownFromCellClick.js
//
// The one home for "does this grid click drill the chart down, and into
// what". Both grid+chart pages wire it into AG Grid's onCellClicked; each
// passes the field names its own rows use, and nothing else about the rule
// is per-page.
//
// Two conditions. A product header row carries none of the identity fields
// (it is a product with its `child_count` and sums — ForecastViewSet._headers),
// so drilling into one would produce an all-undefined scope, and the chart
// would silently fetch it.
//
//   1. Leaf rows only — group headers (child_count present) never drill.
//   2. Identity columns only — item code, item name, SKU, location name. The
//      merchant is editing in the other columns, and a stray click there
//      swapping the chart's scope reads as the app losing its place. On
//      image-capable stores the Product cell IS the name column (its colDef
//      field is the name field — see productIdentity.productColumn), and the
//      grouped mode shows SKU instead of the code, so both spellings of
//      "the identity column" are covered by the same list.
//
// The scope carries `sku` alongside the code because it is what NAMES the
// item on screen: DrillDownChip labels a Shopify row "name (SKU)", the
// GID-suffix item code being a value the merchant never sees. `itemCode`
// stays because it is the chart's wire key.

/**
 * @param {object} event      AG Grid cell-click event.
 * @param {object} fields     Row field names: {itemCode, itemName, sku, locationCode, locationName}.
 * @returns {object|null}     Drill-down scope for useGridChartScope's
 *                            handleRowSelect, or null when the click does
 *                            not drill.
 */
export function drillDownFromCellClick(event, fields) {
  const row = event?.data;
  if (!row) return null;
  if (row.child_count != null) return null;

  // A column with no `field` (action columns, the group column) reads as
  // undefined, and so does an identity field a grid does not declare — the
  // filter keeps those two undefineds from matching each other.
  const clicked = event.colDef?.field;
  const identityFields = [fields.itemCode, fields.itemName, fields.sku, fields.locationName]
    .filter((f) => f != null);
  if (!identityFields.includes(clicked)) return null;

  return {
    itemCode: row[fields.itemCode],
    itemName: row[fields.itemName],
    sku: row[fields.sku],
    locationCode: row[fields.locationCode],
    locationName: row[fields.locationName],
  };
}

/**
 * The grid's defaultColDef with the pointer cursor on exactly the cells a
 * click drills (`ag-cell-drilldown`, styles/grid-page.css). Decided by
 * drillDownFromCellClick itself, so the cursor never promises a click that
 * does nothing. AG Grid merges `cellClassRules` into each column (see
 * utils/gridDefaults.js cellStateClassRules), so no column opts in by hand.
 *
 * @param {object} colDef  The grid's defaultColDef.
 * @param {object} fields  The same field names the grid passes to drillDownFromCellClick.
 * @returns {object}
 */
export function withDrillDownCursor(colDef, fields) {
  return {
    ...colDef,
    cellClassRules: {
      ...colDef.cellClassRules,
      'ag-cell-drilldown': (params) => drillDownFromCellClick(params, fields) != null,
    },
  };
}
